import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { InboundFaxMemberDto, InboundFaxUpdateRequest, InboxDto } from '@app/core/model';
import { InboundFaxApiService, NotificationService } from '@app/core/service';
import { AssignMemberModalComponent } from '@app/features/assign-member-modal/assign-member-modal.component';
import { InboundFaxesService } from '@app/pages/inbound-faxes/services/inbound-faxes.service';
import { InboundFaxesTableModel } from '@app/shared/models';
import { DialogConfig } from '@app/shared/utility';
import { ConfirmationService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';

@Injectable()
export class FaxMemberService {
  private readonly confirmationService = inject(ConfirmationService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialogService = inject(DialogService);
  private readonly inboundFaxApiService = inject(InboundFaxApiService);
  private readonly inboundFaxesService = inject(InboundFaxesService);
  private readonly notificationService = inject(NotificationService);

  public handleClick(target: InboundFaxesTableModel, inbox: InboxDto) {
    if (!target.memberId) {
      this.openAssignDialog(target, inbox);
    } else {
      this.openRemoveDialog(target, inbox);
    }
  }

  private openAssignDialog(target: InboundFaxesTableModel, inbox: InboxDto): void {
    this.dialogService
      .open(
        AssignMemberModalComponent,
        DialogConfig(
          'Assign Member',
          {
            tenantId: inbox.tenantLobProduct?.tenantId,
            tenantName: inbox.tenantLobProduct?.tenantName,
            lineOfBusinessId: inbox.tenantLobProduct?.lineOfBusinessId,
          },
          '812px'
        )
      )
      .onClose.subscribe((res: object) => {
        if (res) {
          this.updateAssignedMember(target.inboundFaxId, res['selectedMember'], inbox);
        }
      });
  }

  private openRemoveDialog(target: InboundFaxesTableModel, inbox: InboxDto): void {
    this.confirmationService.confirm({
      acceptLabel: 'Remove',
      rejectLabel: 'Cancel',
      header: 'Remove Assigned Member',
      message: 'Are you sure that you want to remove the assigned member?',
      accept: () => this.updateAssignedMember(target.inboundFaxId, null, inbox),
      reject: () => {},
    });
  }

  private updateAssignedMember(
    inboundFaxId: string,
    member: InboundFaxMemberDto | null,
    inbox: InboxDto,
  ) {
    const inboundFaxUpdateRequest: InboundFaxUpdateRequest = {
      updateId: member ? member.memberId : null,
      member: member,
    };

    this.inboundFaxApiService
      .updateMember(inboundFaxId, inboundFaxUpdateRequest)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          if (!member) {
            this.notificationService.showSuccess(
              'Successfully Removed Assigned Member',
              'Member has been removed from the fax record.'
            );
          } else {
            this.notificationService.showSuccess(
              'Successfully Assigned Member',
              'Member has been assigned to the fax record.'
            );
          }
          this.inboundFaxesService.reload(inbox);
        },
        error: () => {
          if (!member) {
            this.notificationService.showError(
              'Unable to Remove Member',
              'There was an issue removing the member. Please try again later.'
            );
          } else {
            this.notificationService.showError(
              'Unable to Assign Member',
              'There was an issue assigning a member. Please try again later.'
            );
          }
        },
      });
  }
}
